import * as React from "react"
import { array, object, string } from "yup"
import {Divider, Grid, TextField, Typography} from "@mui/material"
import { useAppToast, useValidatorSeeker } from "@ui-seeker/app"
import {AutocompleteAjaxSeeker, DrawerSeeker, SelectSeeker} from "@ui-seeker/core"
import { isEmpty } from '@ui-seeker/utils'

import { DIPSite, DIPSkuGroup, DIPCountry, DIPBrand, DIPSku, DIPMetricSelect } from "@components";
import {retailer} from "@signals";
import {useMonitoringAdminReportsDrawer} from "./@hooks";
import { monitoringAdminReportFilters } from "../@signals";
import {MonthDaysSelect, MonthSelect, TimeSelect, WeekDaysSelect} from "../@components";
import {WEBBACK_V2_API} from "../../../../@constants";



export default function MonitoringAdminReportsDrawer() {

    const { showToast } = useAppToast()
    const { reportFormFilters, handleReportUpdate, DrawerTitle } = useMonitoringAdminReportsDrawer()

    const validatorData = useValidatorSeeker({
        name: reportFormFilters.value?.name,
        zone: reportFormFilters.value?.zone,
        time: reportFormFilters.value?.executions?.['Time Raw']
    }, object({
        name: string().required('Execution Name must be provided'),
        zone: string().required('Time must be provided'),
        time: array().min(1, 'At least one of hour must be provided')
    }))

    const drawerProps = {
        title: DrawerTitle,
        isOpen: !isEmpty(monitoringAdminReportFilters.value),
        controller: () => {
            monitoringAdminReportFilters.value = {}
        },
        width: "40%",
        successProps: {
            onClick: isEmpty(validatorData.errors)
                ? () => {
                    handleReportUpdate()
                    monitoringAdminReportFilters.value = {}
                }
                : () => {
                    showToast.warning('Please ensure all mandatory fields are completed')
                }
        }
    }

    const isDrawerActive = !isEmpty(monitoringAdminReportFilters.value)

    return <DrawerSeeker {...drawerProps}>
        <Grid container spacing={2}>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Execution Name"
                    size="small"
                    value={reportFormFilters.value?.name}
                    onChange={e => {
                        reportFormFilters.value = {...reportFormFilters.value, name: e.target.value}
                    }}
                    sx={{ mt: 3, mb: 1 }}
                    helperText={!!isDrawerActive && validatorData.errors?.name}
                    error={!!isDrawerActive && !!validatorData.errors?.name}
                />
            </Grid>

            <Grid item xs={6}>
                <DIPCountry
                    value={reportFormFilters.value?.countries}
                    onChange={(e) => {
                        reportFormFilters.value = {...reportFormFilters.value, countries: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    multiple
                    disabled={!!retailer.value?.length}
                />
            </Grid>

            <Grid item xs={6}>
                <DIPSite
                    filters={{ country: reportFormFilters.value?.countries  }}
                    multiple
                    sx={{ mb: 1 }}
                    disabled={!!reportFormFilters.value?.countries?.length}
                />
            </Grid>

            <Grid item xs={6}>
                <DIPSkuGroup
                    value={reportFormFilters.value?.skuGroupsID || []}
                    onChange={(e) => {
                        reportFormFilters.value = {...reportFormFilters.value, skuGroupsID: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    disabled={!!reportFormFilters.value?.skuID?.length}
                    multiple
                />
            </Grid>

            <Grid item xs={6}>
                <DIPBrand
                    value={reportFormFilters.value?.brandID}
                    onChange={(e) => {
                        reportFormFilters.value = {...reportFormFilters.value, brandID: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                />
            </Grid>

            <Grid item xs={12}>
                <DIPSku
                    value={reportFormFilters.value?.skuID || []}
                    filters={{ brand: reportFormFilters.value.brandID || [], skuGroup: reportFormFilters.value?.skuGroupsID }}
                    onChange={(e) => {
                        reportFormFilters.value = {...reportFormFilters.value, skuID: e.target.value}
                    }}
                    sx={{ mb: 1 }}
                    disabled={!!reportFormFilters.value?.skuGroupsID?.length}
                />
            </Grid>

            <Grid item xs={12}>
                <Typography variant='subtitle1'>
                    Executions Info
                </Typography>

                <Divider sx={{ bgcolor: 'primary.light', fontWeight: 'bold', mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
                <AutocompleteAjaxSeeker
                    urlAjax={`${WEBBACK_V2_API}/monitoring/admin/time-zone`}
                    label="Time Zone"
                    placeholder="Select Time Zone"
                    value={reportFormFilters.value?.zone}
                    onChange={(e) => {
                        reportFormFilters.value = {...reportFormFilters.value, zone: e.target.value}
                    }}
                    multiple={false}
                    error={!!isDrawerActive && validatorData.errors?.zone}
                    sx={{ mb: 2 }}
                />
            </Grid>

            <Grid item xs={12}>
                <TimeSelect
                    value={reportFormFilters.value?.executions?.['Time Raw']}
                    multiple
                    onChange={e => {
                        reportFormFilters.value = {
                            ...reportFormFilters.value,
                            executions: {
                                ...reportFormFilters.value.executions,
                                ["Time Raw"]: e.target.value
                            }
                        }
                    }}
                    error={validatorData.errors?.time}
                    sx={{ mb: 2 }}
                />
            </Grid>

            <Grid item xs={12}>
                <MonthDaysSelect
                    value={reportFormFilters.value?.executions?.['Months Days']}
                    multiple
                    onChange={e => {
                        reportFormFilters.value = {
                            ...reportFormFilters.value,
                            executions: {
                                ...reportFormFilters.value.executions,
                                ["Months Days"]: e.target.value
                            }
                        }
                    }}
                    sx={{ mb: 2 }}
                />
            </Grid>

            <Grid item xs={12}>
                <MonthSelect
                    value={reportFormFilters.value?.executions?.['Months']}
                    multiple
                    onChange={e => {
                        reportFormFilters.value = {
                            ...reportFormFilters.value,
                            executions: {
                                ...reportFormFilters.value.executions,
                                ["Months"]: e.target.value
                            }
                        }
                    }}
                    sx={{ mb: 2 }}
                />
            </Grid>

            <Grid item xs={12}>
                <WeekDaysSelect
                    value={reportFormFilters.value?.executions?.['Week Days']}
                    multiple
                    onChange={e => {
                        reportFormFilters.value = {
                            ...reportFormFilters.value,
                            executions: {
                                ...reportFormFilters.value.executions,
                                ["Week Days"]: e.target.value
                            }
                        }
                    }}
                    sx={{ mb: 2 }}
                />
            </Grid>

        </Grid>
    </DrawerSeeker>
}